import ClientModule from "@/store/modules/client.module";
import { toaster } from "@/utilities/toaster";
import { Guid } from "guid-typescript";
import { Machine } from "xstate";
const clientOverviewMachine = Machine({
    id: "clientOverView",
    context: {
        clientId: Guid.createEmpty(),
    },
    initial: "idle",
    states: {
        idle: {
            always: {
                target: "gettingClientInfo",
            },
        },
        gettingClientInfo: {
            invoke: {
                id: "gettingClientInfo",
                src: (context, event) => {
                    return ClientModule.GetClientDetailById(context.clientId);
                },
                onDone: { target: "loadedClientContactsInfo" },
                onError: { target: "serviceUnavailable" },
            },
        },
        loadedClientContactsInfo: {
            type: "parallel",
            // Tab changes: every region is reset (re-entered at the listed target) before the newly
            // selected tab's data is loaded. Each region needs an explicit target — targeting only one
            // region's child leaves the sibling regions in whatever state they were in.
            on: {
                TAB_RESET: { target: "#clientOverView.loadedClientContactsInfo" },
                CONTACT_LOAD: {
                    target: [
                        "#clientOverView.loadedClientContactsInfo.editPrimaryInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.contactAndTeamInfo.gettingClientContactsInfo",
                        "#clientOverView.loadedClientContactsInfo.riskInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.opportunityInfo.idle",
                    ],
                },
                TEAM_LOAD: {
                    target: [
                        "#clientOverView.loadedClientContactsInfo.editPrimaryInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.contactAndTeamInfo.gettingClientTeamsInfo",
                        "#clientOverView.loadedClientContactsInfo.riskInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.opportunityInfo.idle",
                    ],
                },
                RISK_LOAD: {
                    target: [
                        "#clientOverView.loadedClientContactsInfo.editPrimaryInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.contactAndTeamInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.riskInfo.gettingClientRisksInfo",
                        "#clientOverView.loadedClientContactsInfo.opportunityInfo.idle",
                    ],
                },
                OPPORTUNITY_LOAD: {
                    target: [
                        "#clientOverView.loadedClientContactsInfo.editPrimaryInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.contactAndTeamInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.riskInfo.idle",
                        "#clientOverView.loadedClientContactsInfo.opportunityInfo.gettingClientOpportunitiesInfo",
                    ],
                },
            },
            states: {
                editPrimaryInfo: {
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                UPDATE_CLIENT: { target: "updateClient" },
                            },
                        },
                        updateClient: {
                            on: {
                                UPDATING_CLIENT: { target: "updatingClient" },
                                CLOSE_MODAL: { target: "idle" },
                            },
                        },
                        updatingClient: {
                            invoke: {
                                id: "updatingClient",
                                src: (context, event) => {
                                    return ClientModule.EditClient(event.payLoad);
                                },
                                onDone: { target: "idle", actions: "showSuccessMessage" },
                                onError: { target: "updateClient" },
                            },
                        },
                    },
                },
                contactAndTeamInfo: {
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                ADD_CONTACT: { target: "addContactInfo" },
                                UPDATE_CONTACT: { target: "updateContactInfo" },
                                DELETE_CONTACT: { target: "deleteContactInfo" },
                                VIEW_CONTACT: { target: "viewContactInfo" },
                            },
                        },
                        gettingClientContactsInfo: {
                            invoke: {
                                id: "gettingClientContactsInfo",
                                src: (context, event) => {
                                    return ClientModule.GetClientContacts(context.clientId);
                                },
                                onDone: { target: "idle" },
                                onError: { target: "idle" },
                            },
                        },
                        addContactInfo: {
                            on: {
                                ADDING_CONTACT: { target: "addingContactInfo" },
                                CLOSE_MODAL: { target: "#clientOverView.loadedClientContactsInfo" },
                            },
                        },
                        addingContactInfo: {
                            invoke: {
                                id: "addingContactInfo",
                                src: (context, event) => {
                                    return ClientModule.SaveClientContact(event.payLoad);
                                },
                                // Show the new contact right away; the full list refreshes without a loader.
                                onDone: { target: "idle", actions: ["addSavedContact", "showSuccessMessage", "refreshContactsInBackground"] },
                                onError: { target: "addContactInfo" },
                            },
                        },
                        updateContactInfo: {
                            on: {
                                UPDATING_CONTACT: { target: "updatingContactInfo" },
                                CLOSE_MODAL: { target: "#clientOverView.loadedClientContactsInfo" },
                            },
                        },
                        updatingContactInfo: {
                            invoke: {
                                id: "updatingContactInfo",
                                // A Primary Contact change goes through set-key-contact, ordered around the save so the
                                // server never sees an inactive Primary: removing it runs first, setting it runs after.
                                src: async (context, event) => {
                                    const keyContact = (isKeyContact) => ClientModule.SetKeyContact({ clientId: context.clientId.toString(), contactId: event.payLoad.id.toString(), isKeyContact: isKeyContact });
                                    if (event.keyContactChange === false) {
                                        await keyContact(false);
                                    }
                                    const response = await ClientModule.EditClientContact(event.payLoad);
                                    if (response && event.keyContactChange === true) {
                                        await keyContact(true);
                                    }
                                    return response;
                                },
                                onDone: { target: "gettingClientContactsInfo", actions: "showSuccessMessage" },
                                onError: { target: "updateContactInfo" },
                            },
                        },
                        deleteContactInfo: {
                            on: {
                                DELETING_CONTACT: { target: "deletingContactInfo" },
                                CLOSE_MODAL: { target: "#clientOverView.loadedClientContactsInfo" },
                            },
                        },
                        deletingContactInfo: {
                            invoke: {
                                id: "deletingContactInfo",
                                src: (context, event) => {
                                    return ClientModule.DeleteClientContactById(event.payLoad);
                                },
                                onDone: { target: "gettingClientContactsInfo", actions: "showSuccessMessage" },
                                onError: { target: "#clientOverView.loadedClientContactsInfo" },
                            },
                        },
                        viewContactInfo: {
                            on: {
                                UPDATE_CONTACT: { target: "updateContactInfo" },
                                CLOSE_MODAL: { target: "#clientOverView.loadedClientContactsInfo" },
                            },
                        },
                        gettingClientTeamsInfo: {
                            invoke: {
                                id: "gettingClientTeamsInfo",
                                src: (context, event) => {
                                    return ClientModule.GetTeams(context.clientId);
                                },
                                onDone: { target: "loadedClientTeamsInfo" },
                                onError: { target: "idle" },
                            },
                        },
                        loadedClientTeamsInfo: {
                            tags: "loadedClientTeamsInfo",
                            on: {
                                ADD_TEAM: { target: "addTeamInfo" },
                                DEACTIVATE_TEAM: { target: "deactivateTeamInfo" },
                                UPDATE_TEAM: { target: "updateTeamInfo" },
                                DELETE_TEAM: { target: "deleteTeamInfo" },
                                VIEW_TEAM: { target: "viewTeamInfo" },
                                ACTIVATE_TEAM: { target: "activateTeamInfo" },
                            },
                        },
                        addTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            on: {
                                ADDING_TEAM: { target: "addingTeamInfo" },
                                CLOSE_MODAL: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        addingTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            invoke: {
                                id: "addingTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.SaveTeam(event.payLoad);
                                },
                                onDone: { target: "gettingClientTeamsInfo", actions: "showSuccessMessage" },
                                onError: { target: "addTeamInfo" },
                            },
                        },
                        updateTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            on: {
                                UPDATING_TEAM: { target: "updatingTeamInfo" },
                                CLOSE_MODAL: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        updatingTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            invoke: {
                                id: "addingTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.SaveTeam(event.payLoad);
                                },
                                onDone: { target: "gettingClientTeamsInfo", actions: "showSuccessMessage" },
                                onError: { target: "updateTeamInfo" },
                            },
                        },
                        deactivateTeamInfo: {
                            tags: ["loadedClientTeamsInfo", "teamDeactivationInfo"],
                            on: {
                                DEACTIVATING_TEAM: { target: "deactivatingTeamInfo" },
                                CLOSE_MODAL: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        deactivatingTeamInfo: {
                            tags: ["loadedClientTeamsInfo", "teamDeactivationInfo"],
                            invoke: {
                                id: "deactivatingTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.DeactivateTeam(event.deactivationReason);
                                },
                                onDone: { target: "gettingClientTeamsInfo", actions: "showSuccessMessage" },
                                onError: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        activateTeamInfo: {
                            tags: ["loadedClientTeamsInfo", "activateTeam"],
                            on: {
                                ACTIVATING_TEAM: { target: "activatingTeamInfo" },
                                CLOSE_MODAL: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        activatingTeamInfo: {
                            invoke: {
                                id: "activatingTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.ActivateTeam(event.teamId);
                                },
                                onDone: { target: "gettingClientTeamsInfo", actions: "showSuccessMessage" },
                                onError: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        deleteTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            on: {
                                DELETING_TEAM: { target: "deletingTeamInfo" },
                                CLOSE_MODAL: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        deletingTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            invoke: {
                                id: "deletingTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.DeleteTeamById(event.payLoad);
                                },
                                onDone: { target: "gettingClientTeamsInfo", actions: "showSuccessMessage" },
                                onError: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        viewTeamInfo: {
                            tags: "loadedClientTeamsInfo",
                            invoke: {
                                id: "viewTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.GetTeamDetailById(event.teamId);
                                },
                                onDone: { target: "viewingTeamInfo" },
                                onError: { target: "loadedClientTeamsInfo" },
                            },
                        },
                        viewingTeamInfo: {
                            tags: "viewingTeamInfo",
                            on: {
                                ADD_MEMBER: { target: "eligibleTeamMember" },
                                UPDATE_MEMBER: { target: "updateTeamMember" },
                                DELETE_MEMBER: { target: "deleteTeamMember" },
                                BACK_TO_TEAMS: { target: "gettingClientTeamsInfo" },
                                VIEW_APEX_PROJECTS: { target: "viewingApexProjects" },
                                VIEW_TEAM_OPERATIONS: { target: "viewingTeamOperations" },
                                VIEW_TEAM_CORRESPONDENCE: { target: "viewingTeamCorrespondence" },
                            },
                        },
                        // Operations sub-tab: TeamOperationsComponent runs its own machine; this only tracks the open tab
                        viewingTeamOperations: {
                            tags: "viewingTeamInfo",
                            on: {
                                BACK_TO_TEAMS: { target: "gettingClientTeamsInfo" },
                                VIEW_MEMBER: { target: "viewingTeamInfo" },
                                VIEW_APEX_PROJECTS: { target: "viewingApexProjects" },
                                VIEW_TEAM_CORRESPONDENCE: { target: "viewingTeamCorrespondence" },
                            },
                        },
                        // Correspondence sub-tab: TeamCorrespondenceComponent runs its own machine; this only tracks the open tab
                        viewingTeamCorrespondence: {
                            tags: "viewingTeamInfo",
                            on: {
                                BACK_TO_TEAMS: { target: "gettingClientTeamsInfo" },
                                VIEW_MEMBER: { target: "viewingTeamInfo" },
                                VIEW_APEX_PROJECTS: { target: "viewingApexProjects" },
                                VIEW_TEAM_OPERATIONS: { target: "viewingTeamOperations" },
                            },
                        },
                        // Re-fetch the open team after a member add/edit/remove, so the list comes back
                        // in the server's order (current members first, ended last, then by name).
                        refreshingTeamInfo: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "refreshingTeamInfo",
                                src: (context, event) => {
                                    return ClientModule.GetTeamDetailById(ClientModule.team.id);
                                },
                                onDone: { target: "viewingTeamInfo" },
                                onError: { target: "viewingTeamInfo" },
                            },
                            on: {
                                BACK_TO_TEAMS: { target: "gettingClientTeamsInfo" },
                            },
                        },
                        eligibleTeamMember: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "eligibleTeamMember",
                                src: (context, event) => {
                                    return ClientModule.GetEligableUserForTeam(event.teamId);
                                },
                                onDone: { target: "addTeamMember" },
                                onError: { target: "viewingTeamInfo" },
                            },
                        },
                        addTeamMember: {
                            tags: "viewingTeamInfo",
                            on: {
                                ADDING_MEMBER: { target: "addingTeamMember" },
                                CLOSE_MODAL: { target: "viewingTeamInfo" },
                            },
                        },
                        addingTeamMember: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "addingTeamMember",
                                src: (context, event) => {
                                    return ClientModule.SaveTeamMember(event.payLoad);
                                },
                                // Show the new member right away, then re-fetch the team (Members table loader) to restore full rows.
                                onDone: { target: "refreshingTeamInfo", actions: ["addSavedTeamMember", "showSuccessMessage"] },
                                onError: { target: "addTeamMember" },
                            },
                        },
                        updateTeamMember: {
                            tags: "viewingTeamInfo",
                            on: {
                                UPDATING_MEMBER: { target: "updatingTeamMember" },
                                CLOSE_MODAL: { target: "viewingTeamInfo" },
                            },
                        },
                        updatingTeamMember: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "updatingTeamMember",
                                src: (context, event) => {
                                    return ClientModule.EditTeamMember(event.payLoad);
                                },
                                onDone: { target: "refreshingTeamInfo", actions: "showSuccessMessage" },
                                onError: { target: "updateTeamMember" },
                            },
                        },
                        deleteTeamMember: {
                            tags: "viewingTeamInfo",
                            on: {
                                DELETING_MEMBER: { target: "deletingTeamMember" },
                                CLOSE_MODAL: { target: "viewingTeamInfo" },
                            },
                        },
                        deletingTeamMember: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "deletingTeamMember",
                                src: (context, event) => {
                                    return ClientModule.DeleteTeamMemberById(event.teamMemberId);
                                },
                                onDone: { target: "refreshingTeamInfo", actions: "showSuccessMessage" },
                                onError: { target: "deleteTeamMember" },
                            },
                        },
                        viewingApexProjects: {
                            tags: "viewingTeamInfo",
                            on: {
                                ADD_APEX_PROJECT: { target: "addApexProject" },
                                UPDATE_APEX_PROJECT: { target: "updateApexProject" },
                                // Re-fetch the cards: the Team Lead / member count may have changed in the team view
                                BACK_TO_TEAMS: { target: "gettingClientTeamsInfo" },
                                SWITCHING_TL: { target: "switchingTL" },
                                VIEW_MEMBER: { target: "viewingTeamInfo" },
                                VIEW_TEAM_OPERATIONS: { target: "viewingTeamOperations" },
                                VIEW_TEAM_CORRESPONDENCE: { target: "viewingTeamCorrespondence" },
                            },
                        },
                        addApexProject: {
                            tags: "viewingTeamInfo",
                            on: {
                                ADDING_APEX_PROJECT: { target: "addingApexProject" },
                                CLOSE_MODAL: { target: "viewingApexProjects" },
                            },
                        },
                        addingApexProject: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "addingApexProject",
                                src: (context, event) => {
                                    return ClientModule.CreateApexProject({ teamId: event.teamId, teamLeadId: event.teamLeadId, project: event.project });
                                },
                                onDone: { target: "viewingApexProjects", actions: "showSuccessMessage" },
                                onError: { target: "addApexProject" },
                            },
                        },
                        updateApexProject: {
                            tags: "viewingTeamInfo",
                            on: {
                                UPDATING_APEX_PROJECT: { target: "updatingApexProject" },
                                CLOSE_MODAL: { target: "viewingApexProjects" },
                            },
                        },
                        updatingApexProject: {
                            tags: "viewingTeamInfo",
                            invoke: {
                                id: "updatingApexProject",
                                src: (context, event) => {
                                    return ClientModule.UpdateApexProject(event.payload);
                                },
                                onDone: { target: "viewingApexProjects", actions: "showSuccessMessage" },
                                onError: { target: "updateApexProject" },
                            },
                        },
                        switchingTL: {},
                    },
                },
                riskInfo: {
                    initial: "idle",
                    states: {
                        idle: {},
                        gettingClientRisksInfo: {
                            invoke: {
                                id: "gettingClientRisksInfo",
                                src: (context, event) => {
                                    return ClientModule.GetClientRisks(context.clientId);
                                },
                                onDone: { target: "loadedClientRisksInfo" },
                                onError: { target: "idle" },
                            },
                        },
                        loadedClientRisksInfo: {
                            on: {
                                ADD_RISK: { target: "addRiskInfo" },
                                UPDATE_RISK: { target: "updateRiskInfo" },
                                DELETE_RISK: { target: "deleteRiskInfo" },
                            },
                        },
                        addRiskInfo: {
                            on: {
                                ADDING_RISK: { target: "addingRiskInfo" },
                                CLOSE_MODAL: { target: "loadedClientRisksInfo" },
                            },
                        },
                        addingRiskInfo: {
                            invoke: {
                                id: "addingRiskInfo",
                                src: (context, event) => {
                                    return ClientModule.AddClientRisk(event.payLoad);
                                },
                                onDone: { target: "gettingClientRisksInfo", actions: "showSuccessMessage" },
                                onError: { target: "addRiskInfo" },
                            },
                        },
                        updateRiskInfo: {
                            on: {
                                UPDATING_RISK: { target: "updatingRiskInfo" },
                                CLOSE_MODAL: { target: "loadedClientRisksInfo" },
                            },
                        },
                        updatingRiskInfo: {
                            invoke: {
                                id: "updatingRiskInfo",
                                src: (context, event) => {
                                    return ClientModule.EditClientRisk(event.payLoad);
                                },
                                onDone: { target: "gettingClientRisksInfo", actions: "showSuccessMessage" },
                                onError: { target: "updateRiskInfo" },
                            },
                        },
                        deleteRiskInfo: {
                            on: {
                                DELETING_RISK: { target: "deletingRiskInfo" },
                                CLOSE_MODAL: { target: "loadedClientRisksInfo" },
                            },
                        },
                        deletingRiskInfo: {
                            invoke: {
                                id: "deletingRiskInfo",
                                src: (context, event) => {
                                    return ClientModule.DeleteClientRiskById(event.payLoad);
                                },
                                onDone: { target: "gettingClientRisksInfo", actions: "showSuccessMessage" },
                                onError: { target: "loadedClientRisksInfo" },
                            },
                        },
                    },
                },
                opportunityInfo: {
                    initial: "idle",
                    states: {
                        idle: {},
                        gettingClientOpportunitiesInfo: {
                            invoke: {
                                id: "gettingClientOpportunitiesInfo",
                                src: (context, event) => {
                                    return ClientModule.GetClientOpportunities(context.clientId);
                                },
                                onDone: { target: "loadedClientOpportunitiesInfo" },
                                onError: { target: "idle" },
                            },
                        },
                        loadedClientOpportunitiesInfo: {
                            on: {
                                ADD_OPPORTUNITY: { target: "addOpportunityInfo" },
                                UPDATE_OPPORTUNITY: { target: "updateOpportunityInfo" },
                                DELETE_OPPORTUNITY: { target: "deleteOpportunityInfo" },
                            },
                        },
                        addOpportunityInfo: {
                            on: {
                                ADDING_OPPORTUNITY: { target: "addingOpportunityInfo" },
                                CLOSE_MODAL: { target: "loadedClientOpportunitiesInfo" },
                            },
                        },
                        addingOpportunityInfo: {
                            invoke: {
                                id: "addingOpportunityInfo",
                                src: (context, event) => {
                                    return ClientModule.AddClientOpportunity(event.payLoad);
                                },
                                onDone: { target: "gettingClientOpportunitiesInfo", actions: "showSuccessMessage" },
                                onError: { target: "addOpportunityInfo" },
                            },
                        },
                        updateOpportunityInfo: {
                            on: {
                                UPDATING_OPPORTUNITY: { target: "updatingOpportunityInfo" },
                                CLOSE_MODAL: { target: "loadedClientOpportunitiesInfo" },
                            },
                        },
                        updatingOpportunityInfo: {
                            invoke: {
                                id: "updatingOpportunityInfo",
                                src: (context, event) => {
                                    return ClientModule.EditClientOpportunity(event.payLoad);
                                },
                                onDone: { target: "gettingClientOpportunitiesInfo", actions: "showSuccessMessage" },
                                onError: { target: "updateOpportunityInfo" },
                            },
                        },
                        deleteOpportunityInfo: {
                            on: {
                                DELETING_OPPORTUNITY: { target: "deletingOpportunityInfo" },
                                CLOSE_MODAL: { target: "loadedClientOpportunitiesInfo" },
                            },
                        },
                        deletingOpportunityInfo: {
                            invoke: {
                                id: "deletingOpportunityInfo",
                                src: (context, event) => {
                                    return ClientModule.DeleteClientOpportunityById(event.payLoad);
                                },
                                onDone: { target: "gettingClientOpportunitiesInfo", actions: "showSuccessMessage" },
                                onError: { target: "loadedClientOpportunitiesInfo" },
                            },
                        },
                    },
                },
            },
        },
        serviceUnavailable: {
            type: "final",
        },
    },
}, {
    actions: {
        showSuccessMessage: (context, event) => {
            toaster.ShowSuccess(event.data.successMessages[0]);
        },
        addSavedContact: (context, event) => {
            if (event.data && event.data.clientContact) {
                ClientModule.AddClientContactToList(event.data.clientContact);
            }
        },
        refreshContactsInBackground: (context) => {
            ClientModule.GetClientContacts(context.clientId).catch(() => null);
        },
        addSavedTeamMember: (context, event) => {
            if (event.data && event.data.teamMember) {
                ClientModule.AddTeamMemberToList(event.data.teamMember);
            }
        },
    },
    guards: {},
    activities: {},
    services: {},
});
export default clientOverviewMachine;
